'use client';

import { useCallback } from 'react';
import { UserAccount, Order } from '@drift-labs/sdk';
import { UISerializableOrder } from '@drift/common';
import { notify } from 'src/utils/notifications';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';

/**
 * Hook for triggering orders
 */
export const useTriggerOrder = () => {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();

	const triggerOrder = useCallback(
		async (order: UISerializableOrder) => {
			if (!driftClient || !currentUserAccount) {
				return;
			}

			const userAccount = currentUserAccount.client?.getUserAccount();
			const pubKey = currentUserAccount.pubKey;
			const sdkOrder = userAccount?.orders.find(
				(o) => o.orderId === order.orderId
			);

			try {
				const txSig = await driftClient.triggerOrder(
					pubKey,
					userAccount as UserAccount,
					sdkOrder as Order,
					getTxParams()
				);

				if (txSig) {
					notify({
						type: 'success',
						message: 'Order triggered.',
						id: `triggered-order-${order.orderId}`,
					});
				} else {
					notify({
						type: 'error',
						message: 'There was an error triggering this order.',
					});
				}
			} catch (err: any) {
				console.error(err);
				notify({
					type: 'error',
					message: 'There was an error triggering this order.',
				});
			}
		},
		[driftClient, currentUserAccount, getTxParams]
	);

	return { triggerOrder };
};
